import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { z } from "zod";

// Validation schema for year input
const yearSchema = z.number().int().min(1900, { message: "El año debe ser 1900 o posterior" }).max(new Date().getFullYear(), { message: "El año no puede ser futuro" });

const ChineseZodiacCalculator = () => {
  const [year, setYear] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [result, setResult] = useState<{
    animal: string;
    emoji: string;
    element: string;
    elementColor: string;
    animalPath: string;
  } | null>(null);

  const chineseZodiacAnimals = [
    { name: "Rata", emoji: "🐀", path: "/horoscopo-rata" },
    { name: "Buey", emoji: "🐂", path: "/horoscopo-buey" },
    { name: "Tigre", emoji: "🐅", path: "/horoscopo-tigre" },
    { name: "Conejo", emoji: "🐇", path: "/horoscopo-conejo" },
    { name: "Dragón", emoji: "🐉", path: "/horoscopo-dragon" },
    { name: "Serpiente", emoji: "🐍", path: "/horoscopo-serpiente" },
    { name: "Caballo", emoji: "🐎", path: "/horoscopo-caballo" },
    { name: "Cabra", emoji: "🐐", path: "/horoscopo-cabra" },
    { name: "Mono", emoji: "🐒", path: "/horoscopo-mono" },
    { name: "Gallo", emoji: "🐓", path: "/horoscopo-gallo" },
    { name: "Perro", emoji: "🐕", path: "/horoscopo-perro" },
    { name: "Cerdo", emoji: "🐖", path: "/horoscopo-cerdo" },
  ];

  const chineseElements = [
    { name: "Metal", color: "text-slate-400" },
    { name: "Agua", color: "text-blue-400" },
    { name: "Madera", color: "text-green-400" },
    { name: "Fuego", color: "text-red-400" },
    { name: "Tierra", color: "text-yellow-600" },
  ];

  const calculateChineseZodiac = (birthYear: number) => {
    // Calculate animal (cycle of 12 years, Rat starts at 1924)
    const animalIndex = (birthYear - 1924) % 12;
    const animal = chineseZodiacAnimals[animalIndex];
    
    // Calculate element (cycle of 10 years, 2 years per element)
    const elementIndex = Math.floor(((birthYear - 1924) % 10) / 2);
    const element = chineseElements[elementIndex];
    
    setResult({
      animal: animal.name,
      emoji: animal.emoji,
      element: element.name,
      elementColor: element.color,
      animalPath: animal.path,
    });
  };

  const validateAndCalculate = (value: string) => {
    // Clear previous error and result
    setError("");
    setResult(null);

    if (!value || value.trim() === "") {
      return;
    }

    // Parse and validate the year
    const yearNum = parseInt(value, 10);
    
    // Check for invalid number
    if (isNaN(yearNum)) {
      setError("Por favor ingresa un año válido");
      return;
    }

    // Validate with zod schema
    const validation = yearSchema.safeParse(yearNum);
    
    if (!validation.success) {
      setError(validation.error.errors[0].message);
      return;
    }

    // If validation passes, calculate the zodiac
    calculateChineseZodiac(yearNum);
  };

  const handleYearChange = (value: string) => {
    setYear(value);
    validateAndCalculate(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setYear(value);
    validateAndCalculate(value);
  };

  // Generate year options from current year back to 1900
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 1900; year--) {
      years.push(year);
    }
    return years;
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="lg" className="text-lg">
          ¿Cuál es mi Animal en el Horóscopo Chino?
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-xl text-center">
            Descubre tu Signo Chino
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Año de nacimiento:
            </label>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Escribe el año"
                value={year}
                onChange={handleInputChange}
                min={1900}
                max={new Date().getFullYear()}
                className={cn("text-center", error && "border-red-500")}
              />
              <Select value={year} onValueChange={handleYearChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="O selecciona" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {generateYearOptions().map((y) => (
                    <SelectItem key={y} value={y.toString()}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}
          </div>

          {result && (
            <Card className="gradient-celestial">
              <CardContent className="p-4 text-center space-y-3">
                <div className="text-5xl">{result.emoji}</div>
                <div>
                  <h3 className="text-xl font-bold">
                    {result.animal}
                  </h3>
                  <p className="text-sm">
                    Elemento:{" "}
                    <span className={cn("font-bold", result.elementColor)}>
                      {result.element}
                    </span>
                  </p>
                </div>
                <Link to={result.animalPath}>
                  <Button variant="outline" size="sm" className="w-full">
                    Ver Horóscopo Completo
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChineseZodiacCalculator;
